import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays, isNotNil } from '@malou-io/package-utils';

import { AggregatedKeywordRankingData } from '#modules/statistics-seo/models/aggregated-seo/keyword-table-data-row';
import {
    AbstractCsvService,
    DataWithNilExcluded,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';

interface Data {
    keywordRankings: AggregatedKeywordRankingData[];
    dates: { startDate: string; endDate: string };
}

@Injectable()
export class AggregatedKeywordsService extends AbstractCsvService<Data> {
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected _getData$(): Observable<Data> {
        const selectKeywordRankings = this._aggregatedStatisticsStore.keywordRankingsData();
        const filters = this._aggregatedStatisticsStore.filters();
        const monthYearPeriod = filters.monthYearPeriod;
        const startDate = DateTime.fromObject(monthYearPeriod.startMonthYear).startOf('month').toISODate();
        const endDate = DateTime.fromObject(monthYearPeriod.endMonthYear).endOf('month').toISODate();
        return of({
            keywordRankings: selectKeywordRankings,
            dates: { startDate, endDate },
        });
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getCsvHeaderRow(): string[] {
        return [
            'Start Date',
            'End Date',
            'Restaurant Name',
            'Position Google Top 20',
            'Position Google Top 20 Evolution',
            'Position Google Top 3',
            'Position Google Top 3 Evolution',
            'Google Search Discovery',
            'Google Search Discovery Evolution',
            'Google Search Notoriety',
            'Google Search Notoriety Evolution',
        ];
    }

    protected override _getCsvDataRows(data: DataWithNilExcluded<Data>): CsvAsStringArrays {
        const { keywordRankings, dates } = data;
        const { startDate, endDate } = dates;

        return keywordRankings.map((keywordRanking) => {
            const restaurantName = keywordRanking.restaurantName;
            const positionGoogleTop20 = keywordRanking.currentTop20?.toString() ?? '-';
            const positionGoogleTop20Evolution = keywordRanking.top20Evolution?.toString() ?? '-';
            const positionGoogleTop3 = keywordRanking.currentTop3?.toString() ?? '-';
            const positionGoogleTop3Evolution = keywordRanking.top3Evolution?.toString() ?? '-';
            const googleSearchDiscovery = keywordRanking.discoveryKeywordImpressions?.toString() ?? '-';
            const googleSearchDiscoveryEvolution = this._computeEvolution(
                keywordRanking.discoveryKeywordImpressions,
                keywordRanking.previousDiscoveryKeywordImpressions
            );
            const googleSearchNotoreity = keywordRanking.brandingKeywordImpressions?.toString() ?? '-';
            const googleSearchNotoreityEvolution = this._computeEvolution(
                keywordRanking.brandingKeywordImpressions,
                keywordRanking.previousBrandingKeywordImpressions
            );

            return [
                startDate,
                endDate,
                restaurantName,
                positionGoogleTop20,
                positionGoogleTop20Evolution,
                positionGoogleTop3,
                positionGoogleTop3Evolution,
                googleSearchDiscovery,
                googleSearchDiscoveryEvolution,
                googleSearchNotoreity,
                googleSearchNotoreityEvolution,
            ];
        });
    }

    private _computeEvolution(current: number | undefined, previous: number | undefined): string {
        if (isNotNil(current) && isNotNil(previous)) {
            return (current - previous).toString();
        }
        return '-';
    }
}
